import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SkillNode = ({ 
  skill, 
  position, 
  size, 
  isActive, 
  onClick, 
  connections = [],
  category 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const nodeRef = useRef(null);

  const getCategoryStyle = () => {
    switch (category) {
      case 'technical':
        return {
          shape: 'polygon(50% 0%, 0% 100%, 100% 100%)',
          gradient: 'from-accent to-blue-400',
          glow: 'rgba(0, 212, 255, 0.4)'
        };
      case 'creative':
        return {
          shape: 'circle(50%)',
          gradient: 'from-cosmic-energy to-pink-400',
          glow: 'rgba(255, 107, 53, 0.4)'
        };
      case 'hybrid':
        return {
          shape: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
          gradient: 'from-cosmic-cta to-purple-400',
          glow: 'rgba(255, 51, 102, 0.4)'
        };
      default:
        return {
          shape: 'circle(50%)',
          gradient: 'from-accent to-cosmic-energy',
          glow: 'rgba(0, 212, 255, 0.3)'
        };
    }
  };

  const categoryStyle = getCategoryStyle();
  const nodeSize = size === 'large' ? 'w-20 h-20' : size === 'medium' ? 'w-14 h-14' : 'w-10 h-10';
  const iconSize = size === 'large' ? 32 : size === 'medium' ? 24 : 16;

  return (
    <motion.div
      ref={nodeRef}
      className={`absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 ${nodeSize}`}
      style={{
        left: `${position?.x}%`,
        top: `${position?.y}%`,
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: 1, 
        opacity: 1,
        rotate: category === 'technical' ? 0 : category === 'creative' ? 360 : 180
      }}
      whileHover={{ scale: 1.2 }}
      whileTap={{ scale: 0.95 }}
      transition={{ 
        duration: 0.6,
        rotate: { duration: 20, repeat: Infinity, ease: "linear" }
      }}
      onClick={() => onClick(skill)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow Effect */}
      <div 
        className={`absolute inset-0 bg-gradient-to-r ${categoryStyle?.gradient} rounded-full blur-md opacity-60 animate-pulse-glow`}
        style={{ 
          clipPath: categoryStyle?.shape,
          boxShadow: `0 0 30px ${categoryStyle?.glow}`
        }}
      />
      {/* Main Node */}
      <div 
        className={`relative w-full h-full bg-gradient-to-r ${categoryStyle?.gradient} flex items-center justify-center transition-all duration-300 ${
          isActive ? 'ring-2 ring-accent ring-offset-2 ring-offset-background' : ''
        }`}
        style={{ clipPath: categoryStyle?.shape }}
      >
        <Icon 
          name={skill?.icon} 
          size={iconSize} 
          className="text-background drop-shadow-lg" 
        />
      </div>
      {/* Skill Level Indicator */}
      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
        <div className="flex space-x-1">
          {[...Array(5)]?.map((_, i) => (
            <div
              key={i}
              className={`w-1 h-1 rounded-full ${
                i < skill?.level ? 'bg-accent' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>
      {/* Hover Label */}
      {isHovered && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-10"
        >
          <div className="glass-panel px-3 py-1 rounded-lg whitespace-nowrap">
            <p className="font-body text-sm font-medium text-foreground">
              {skill?.name}
            </p>
            <p className="font-body text-xs text-muted-foreground">
              {skill?.experience}
            </p>
          </div>
        </motion.div>
      )}
      {/* Recent Acquisition Pulse */}
      {skill?.isRecent && (
        <div className="absolute inset-0 rounded-full border-2 border-cosmic-cta animate-ping opacity-75" />
      )}
    </motion.div>
  );
};

export default SkillNode;